import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getIncidents, getIncidentReview } from '../../shared/api/api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import useResizableColumns, { ICON_COL_MIN_WIDTH } from '../../shared/hooks/useResizableColumns';
import '../../shared/styles/resizableTable.css';
import './PostIncidentReviewsPage.css';

const PIR_COLUMNS = [
  { key: 'reference', label: 'Reference', width: 130 },
  { key: 'service', label: 'Service', width: 140 },
  { key: 'severity', label: 'Severity', width: 100 },
  { key: 'status', label: 'Status', width: 110 },
  { key: 'resolvedAt', label: 'Resolved At', width: 150 },
  { key: 'pirStatus', label: 'PIR Status', width: 130 },
  { key: 'reviewedBy', label: 'Reviewed By', width: 130 },
  { key: 'reviewedAt', label: 'Reviewed At', width: 150 },
  { key: 'action', label: '', width: 130, minWidth: ICON_COL_MIN_WIDTH },
];

/**
 * Post-Incident Reviews — lists all RESOLVED/CLOSED incidents
 * and shows whether a PIR has been completed for each.
 */
function PostIncidentReviewsPage() {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [reviews, setReviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('ALL'); // ALL | COMPLETE | PENDING
  const [sortField, setSortField] = useState('resolvedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const { colWidths, tableWidth, handleResizeStart } = useResizableColumns(PIR_COLUMNS);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getIncidents();
      const all = Array.isArray(res.data) ? res.data : res.data?.incidents || [];
      const resolved = all.filter((i) => i.status === 'RESOLVED' || i.status === 'CLOSED');

      // Fetch review status for each resolved/closed incident in parallel
      const reviewResults = await Promise.allSettled(
        resolved.map((i) => getIncidentReview(i.service, i.incident_id))
      );
      const reviewMap = {};
      reviewResults.forEach((result, idx) => {
        const incidentId = resolved[idx].incident_id;
        if (result.status === 'fulfilled' && result.value.status === 200 && result.value.data) {
          reviewMap[incidentId] = result.value.data;
        } else {
          reviewMap[incidentId] = null;
        }
      });

      // Set together (TSB-4570) — incidents and reviews must land in the same
      // render. Setting incidents first left a render where the stats were
      // computed from the new incident count against the stale/empty reviews
      // map, so the totals flashed a wrong value before settling.
      setIncidents(resolved);
      setReviews(reviewMap);
    } catch {
      setError('Failed to load post-incident reviews.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const DATE_SORT_FIELDS = ['resolvedAt', 'reviewedAt'];

  const getSortValue = (incident, field) => {
    const review = reviews[incident.incident_id];
    switch (field) {
      case 'reference': return incident.reference_number || incident.incident_id || '';
      case 'service': return incident.service || '';
      case 'severity': return incident.severity || '';
      case 'status': return incident.status || '';
      case 'resolvedAt': return incident.resolved_at || incident.updated_at;
      case 'reviewedBy': return review?.reviewedBy || '';
      case 'reviewedAt': return review?.reviewedAt;
      default: return '';
    }
  };

  const filteredIncidents = incidents
    .filter((i) => {
      if (filter === 'COMPLETE') return reviews[i.incident_id] != null;
      if (filter === 'PENDING') return reviews[i.incident_id] == null;
      return true;
    })
    .sort((a, b) => {
      let aVal = getSortValue(a, sortField);
      let bVal = getSortValue(b, sortField);
      if (DATE_SORT_FIELDS.includes(sortField)) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(DATE_SORT_FIELDS.includes(field) ? 'desc' : 'asc');
    }
  };

  const getSortIndicator = (field) => {
    const icon = sortField === field ? (sortDirection === 'asc' ? '▲' : '▼') : '';
    return <span className="incidents-sort-icon">{icon}</span>;
  };

  const SORTABLE_COLUMNS = new Set(['reference', 'service', 'severity', 'status', 'resolvedAt', 'reviewedBy', 'reviewedAt']);

  const completeCount = incidents.filter((i) => reviews[i.incident_id] != null).length;
  const pendingCount = incidents.filter((i) => reviews[i.incident_id] == null).length;

  const formatDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('en-ZA', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  const getSeverityClass = (severity) => {
    const s = (severity || '').toUpperCase();
    if (s === 'CRITICAL') return 'pir-severity pir-severity--critical';
    if (s === 'HIGH') return 'pir-severity pir-severity--high';
    if (s === 'MEDIUM') return 'pir-severity pir-severity--medium';
    return 'pir-severity pir-severity--low';
  };

  return (
    <div className="page-container">
      {loading && <LoadingSpinner message="Loading reviews..." />}

      <div className="pir-header">
        <div>
          <h1 className="page-title">Post-Incident Reviews</h1>
          <p className="pir-subtitle">Track RCA completion for resolved and closed incidents.</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchData}>Refresh</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Stats row */}
      <div className="pir-stats">
        <div className="pir-stat-card pir-stat-card--total">
          <span className="pir-stat-value">{incidents.length}</span>
          <span className="pir-stat-label">Total Resolved</span>
        </div>
        <div className="pir-stat-card pir-stat-card--complete">
          <span className="pir-stat-value">{completeCount}</span>
          <span className="pir-stat-label">PIR Complete</span>
        </div>
        <div className="pir-stat-card pir-stat-card--pending">
          <span className="pir-stat-value">{pendingCount}</span>
          <span className="pir-stat-label">PIR Pending</span>
        </div>
        <div className="pir-stat-card pir-stat-card--rate">
          <span className="pir-stat-value">
            {incidents.length > 0 ? Math.round((completeCount / incidents.length) * 100) : 0}%
          </span>
          <span className="pir-stat-label">Completion Rate</span>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="pir-filters">
        {['ALL', 'COMPLETE', 'PENDING'].map((f) => (
          <button
            key={f}
            className={`pir-filter-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'ALL' ? `All (${incidents.length})` : f === 'COMPLETE' ? `Complete (${completeCount})` : `Pending (${pendingCount})`}
          </button>
        ))}
      </div>

      {/* Table */}
      {!loading && filteredIncidents.length === 0 ? (
        <div className="card">
          <p className="empty-state">
            {filter === 'COMPLETE' ? 'No completed reviews yet.' : filter === 'PENDING' ? 'No pending reviews.' : 'No resolved or closed incidents found.'}
          </p>
        </div>
      ) : (
        <div className="card pir-table-card">
          <table className="pir-table resizable-table" style={{ width: tableWidth }}>
            <colgroup>
              {PIR_COLUMNS.map((c) => (
                <col key={c.key} style={{ width: `${colWidths[c.key]}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {PIR_COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className={`resizable-th${SORTABLE_COLUMNS.has(c.key) ? ' incidents-th-sortable' : ''}`}
                    onClick={SORTABLE_COLUMNS.has(c.key) ? () => handleSort(c.key) : undefined}
                  >
                    {c.label}
                    {SORTABLE_COLUMNS.has(c.key) && getSortIndicator(c.key)}
                    <span className="col-resize-handle" onMouseDown={handleResizeStart(c.key)} onClick={(e) => e.stopPropagation()} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredIncidents.map((incident) => {
                const review = reviews[incident.incident_id];
                return (
                  <tr key={incident.incident_id}>
                    <td className="pir-ref">{incident.reference_number || incident.incident_id}</td>
                    <td>{incident.service || '—'}</td>
                    <td>
                      <span className={getSeverityClass(incident.severity)}>
                        {incident.severity || '—'}
                      </span>
                    </td>
                    <td>
                      <span className={`pir-status pir-status--${(incident.status || '').toLowerCase()}`}>
                        {incident.status}
                      </span>
                    </td>
                    <td>{formatDate(incident.resolved_at || incident.updated_at)}</td>
                    <td>
                      {review ? (
                        <span className="pir-badge pir-badge--complete">PIR Complete</span>
                      ) : (
                        <span className="pir-badge pir-badge--pending">PIR Pending</span>
                      )}
                    </td>
                    <td>{review?.reviewedBy || '—'}</td>
                    <td>{review ? formatDate(review.reviewedAt) : '—'}</td>
                    <td>
                      <button
                        className="btn-link"
                        onClick={() => navigate(`/incidents/${incident.service}/${incident.incident_id}`)}
                      >
                        {review ? 'View Review →' : 'Add Review →'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PostIncidentReviewsPage;
